import { BookingConfig } from 'patient-api-lib'
import { ThemeType } from '../themes/themeContract.css'
import { mix } from 'polished'

const setColor = (
  themePresetValue: string | undefined,
  overridingParamValue: string | undefined,
  defaultValue: string | undefined
) => {
  return overridingParamValue ?? themePresetValue ?? (defaultValue || '')
}

export const setCustomizableTheme = (
  theme: ThemeType,
  overridingParams: { colors: BookingConfig.Theme.Colors.AsObject }
) => {
  const { color, font, externalHeader, widgetDimensions } = theme
  const {
    button: buttonColor,
    iconButton: iconButtonColor,
    link: linkColor,
    option: optionColor,
    calendar: calendarColor,
    scroll: scrollColor,
    groupDivider: groupDividerColor,
    primary: primaryColor,
    secondary: secondaryColor,
    accent: accentColor,
    topBar: topBarColor,
    screen: screenColor,
    clinicCard: clinicCardColor,
    appointmentCard: appointmentCardColor,
    summaryBlock: summaryBlockColor,
    dateSelector: dateSelectorColor,
    modal: modalColor,
    jumbotron: jumbotronColor,
    notFound: notFoundColor,
    progressBar: progressBarColor,
    screenLink: screenLinkColor,
    alerts: alertsColor,
  } = color

  return {
    color: {
      ...color,
      backgroundColor: setColor(
        color.backgroundColor,
        overridingParams.colors?.background?.value,
        undefined
      ),
      topBar: {
        ...topBarColor,
        background: setColor(
          color.topBar.background,
          overridingParams.colors?.background?.value,
          undefined
        ),
        text: setColor(
          color.topBar.text,
          overridingParams.colors?.screenTitle?.value,
          undefined
        ),
      },
      button: {
        ...buttonColor,
        primary: {
          text: setColor(
            buttonColor.primary.text,
            overridingParams.colors?.buttonText?.value,
            accentColor
          ),
          background: setColor(
            buttonColor.primary.background,
            overridingParams.colors?.buttonBackground?.value,
            primaryColor
          ),
          border: setColor(buttonColor.primary.border, undefined, primaryColor),
        },
        secondary: {
          ...buttonColor.secondary,
          text: setColor(buttonColor.secondary.text, undefined, accentColor),
        },
      },
      link: {
        ...linkColor,
        text: setColor(linkColor.text, undefined, primaryColor),
      },
      option: {
        ...optionColor,
        background: setColor(
          optionColor.background,
          overridingParams.colors?.cardBackground?.value,
          undefined
        ),
        shadow: setColor(
          optionColor.shadow,
          overridingParams.colors?.cardBackground?.value,
          primaryColor
        ),
        name: {
          text: setColor(
            optionColor.name.text,
            overridingParams.colors?.cardTitle?.value,
            secondaryColor
          ),
        },
        description: {
          text: setColor(
            optionColor.description.text,
            overridingParams.colors?.cardText?.value,
            primaryColor
          ),
        },
      },
      screen: {
        ...screenColor,
        title: {
          text: setColor(
            color.screen.title.text,
            overridingParams.colors?.screenTitle?.value,
            undefined
          ),
        },
        subtitle: {
          text: setColor(
            color.screen.subtitle.text,
            overridingParams.colors?.screenSubtitle?.value,
            undefined
          ),
        },
      },
      clinicCard: {
        ...clinicCardColor,
        background: setColor(
          color.clinicCard.background,
          overridingParams.colors?.cardBackground?.value,
          undefined
        ),
        shadow: setColor(
          color.clinicCard.shadow,
          overridingParams.colors?.cardBackground?.value,
          primaryColor
        ),
        name: {
          text: setColor(
            color.clinicCard.name.text,
            overridingParams.colors?.cardTitle?.value,
            secondaryColor
          ),
        },
        address: {
          text: setColor(
            color.clinicCard.address.text,
            overridingParams.colors?.cardText?.value,
            primaryColor
          ),
        },
        availableTimes: {
          text: setColor(
            color.clinicCard.availableTimes.text,
            overridingParams.colors?.cardTitle?.value,
            primaryColor
          ),
        },
        distance: {
          text: setColor(
            color.clinicCard.distance.text,
            overridingParams.colors?.cardText?.value,
            primaryColor
          ),
        },
      },
      appointmentCard: {
        ...appointmentCardColor,
        background: setColor(
          color.appointmentCard.background,
          overridingParams.colors?.cardBackground?.value,
          undefined
        ),
        shadow: setColor(
          color.appointmentCard.shadow,
          overridingParams.colors?.cardBackground?.value,
          primaryColor
        ),
        time: {
          text: setColor(
            color.appointmentCard.time.text,
            overridingParams.colors?.cardTitle?.value,
            secondaryColor
          ),
        },
        doctor: {
          text: setColor(
            color.appointmentCard.doctor.text,
            overridingParams.colors?.cardText?.value,
            primaryColor
          ),
        },
      },
      summaryBlock: {
        ...summaryBlockColor,
        title: {
          text: setColor(
            color.summaryBlock.title.text,
            overridingParams.colors?.screenTitle?.value,
            secondaryColor
          ),
        },
        text: {
          text: setColor(
            color.summaryBlock.text.text,
            overridingParams.colors?.screenSubtitle?.value,
            primaryColor
          ),
        },
        additionalText: {
          text: setColor(
            color.summaryBlock.additionalText.text,
            overridingParams.colors?.screenSubtitle?.value,
            primaryColor
          ),
        },
        border: setColor(
          color.summaryBlock.border,
          overridingParams.colors?.groupDivider?.value,
          secondaryColor
        ),
        background: overridingParams.colors?.notFoundBackground?.value
          ? mix(
              '0.5',
              overridingParams.colors?.notFoundBackground?.value,
              '#d7e6e1'
            )
          : '#d7e6e1',
      },
      dateSelector: {
        ...dateSelectorColor,
        text: setColor(
          color.dateSelector.text,
          overridingParams.colors?.screenTitle?.value,
          undefined
        ),
        border: setColor(
          color.dateSelector.border,
          overridingParams.colors?.screenTitle?.value,
          undefined
        ),
      },
      modal: {
        ...modalColor,
        background: setColor(
          color.modal.background,
          overridingParams.colors?.background?.value,
          undefined
        ),
        title: {
          ...modalColor.title,
          text: setColor(
            color.modal.title.text,
            overridingParams.colors?.screenTitle?.value,
            undefined
          ),
        },
      },
      calendar: {
        ...calendarColor,
        cell: {
          text: setColor(
            calendarColor.cell.text,
            overridingParams.colors?.calendarCellText?.value,
            undefined
          ),
        },
        dayHover: {
          ...calendarColor.dayHover,
          background: setColor(
            calendarColor.dayHover.background,
            overridingParams.colors?.calendarActiveBackground?.value,
            primaryColor
          ),
          text: setColor(
            calendarColor.dayHover.text,
            overridingParams.colors?.calendarActiveText?.value,
            secondaryColor
          ),
        },
        daySelected: {
          ...calendarColor.daySelected,
          background: setColor(
            calendarColor.daySelected.background,
            overridingParams.colors?.calendarActiveBackground?.value,
            primaryColor
          ),
          text: setColor(
            calendarColor.dayHover.text,
            overridingParams.colors?.calendarActiveText?.value,
            secondaryColor
          ),
        },
      },
      iconButton: {
        ...iconButtonColor,
        text: setColor(
          iconButtonColor.text,
          overridingParams.colors?.filterButtonText?.value,
          secondaryColor
        ),
        border: setColor(
          iconButtonColor.border,
          overridingParams.colors?.filterButtonText?.value,
          secondaryColor
        ),
      },
      jumbotron: {
        ...jumbotronColor,
        title: {
          text: setColor(
            color.jumbotron.title.text,
            overridingParams.colors?.jumbotronTitle?.value,
            undefined
          ),
        },
        feature: {
          text: setColor(
            color.jumbotron.feature.text,
            overridingParams.colors?.jumbotronText?.value,
            undefined
          ),
        },
      },
      scroll: {
        ...scrollColor,
        thumb: {
          background: setColor(
            scrollColor.thumb.background,
            overridingParams.colors?.scrollbarThumb?.value,
            primaryColor
          ),
        },
        track: {
          background: setColor(
            scrollColor.track.background,
            overridingParams.colors?.scrollbarTrack?.value,
            undefined
          ),
        },
      },
      groupDivider: {
        ...groupDividerColor,
        background: setColor(
          groupDividerColor.background,
          overridingParams.colors?.groupDivider?.value,
          undefined
        ),
      },
      notFound: {
        ...notFoundColor,
        container: {
          background: setColor(
            color.notFound.container.background,
            overridingParams.colors?.notFoundBackground?.value,
            undefined
          ),
        },
        subtitle: {
          text: setColor(
            color.notFound.subtitle.text,
            overridingParams.colors?.notFoundText?.value,
            undefined
          ),
        },
      },
      progressBar: {
        ...progressBarColor,
        text: setColor(
          color.progressBar.text,
          overridingParams.colors?.progressBarText?.value,
          undefined
        ),
        label: setColor(
          color.progressBar.text,
          overridingParams.colors?.progressBarLabel?.value,
          undefined
        ),
        activeBackground: setColor(
          color.progressBar.activeBackground,
          overridingParams.colors?.progressBarActive?.value,
          primaryColor
        ),
        inactiveBackground: setColor(
          color.progressBar.inactiveBackground,
          overridingParams.colors?.progressBarInactive?.value,
          undefined
        ),
      },
      screenLink: {
        ...screenLinkColor,
        text: setColor(
          color.screenLink.text,
          overridingParams.colors?.screenTitle?.value,
          undefined
        ),
      },
      alerts: {
        success: {
          background: setColor(
            alertsColor?.success?.background,
            overridingParams.colors?.cardBackground?.value,
            undefined
          ),
          text: setColor(
            alertsColor?.success?.text,
            overridingParams.colors?.cardTitle?.value,
            undefined
          ),
        },
        warning: {
          ...alertsColor.warning,
        },
      },
    },
    font: {
      ...font,
    },
    externalHeader: {
      ...externalHeader,
    },
    widgetDimensions: {
      ...widgetDimensions,
    },
  }
}
